<template>
  <div
    class="restriktionen-month card card-custom"
    :class="{ 'restriktionen-month--kompakt': weeks.length > 5 }"
  >
    <!-- Kopfzeile mit den Wochentagsnamen, Montag als Wochenstart -->
    <div class="restriktionen-month__weekdays">
      <div v-for="weekday in weekdays" :key="weekday" class="restriktionen-month__weekday">
        {{ weekday }}
      </div>
    </div>

    <!-- Wochenraster des Monats -->
    <div class="restriktionen-month__grid">
      <div v-for="(week, weekIndex) in weeks" :key="weekIndex" class="restriktionen-month__week">
        <div
          v-for="day in week"
          :key="day.key"
          class="restriktionen-month__day"
          :class="{
            'restriktionen-month__day--outside': !day.inCurrentMonth,
            'restriktionen-month__day--has-restriktion': !!infosProTag[day.key],
          }"
          @dblclick="onDayDoubleClick(day)"
        >
          <span
            class="restriktionen-month__day-number"
            :class="{ 'restriktionen-month__day-number--today': day.isToday }"
          >
            {{ day.dayNumber }}
          </span>

          <div v-if="infosProTag[day.key]" class="restriktionen-month__badge">
            <RestriktionenBadge
              :type="infosProTag[day.key].primary.type"
              :count="infosProTag[day.key].extraCount"
              :entries="infosProTag[day.key].entries"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RestriktionenBadge from './restriktionen-badge.vue';
import {
  buildMonthMatrix,
  getWochenAnzahl,
  groupRestriktionenByDay,
  weekdaysLong,
} from './restriktionen-calendar.helper.js';
import { sortRestriktionenByPriority } from './restriktionen.constants.js';

export default {
  name: 'RestriktionenMonthViewComponent',
  components: {
    RestriktionenBadge,
  },
  props: {
    date: {
      type: [Object, String, Date],
      required: true,
    },
    restriktionen: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    weekdays() {
      return weekdaysLong;
    },
    // nur die vom Monat tatsächlich belegten Wochenreihen benutzen
    weeks() {
      return buildMonthMatrix(this.date, getWochenAnzahl(this.date));
    },
    restriktionenByDay() {
      return groupRestriktionenByDay(this.restriktionen);
    },
    infosProTag() {
      return Object.keys(this.restriktionenByDay).reduce((acc, key) => {
        const entries = sortRestriktionenByPriority(this.restriktionenByDay[key]);
        const gesamt = entries.reduce((sum, entry) => sum + 1 + (entry.count || 0), 0);
        acc[key] = { entries, primary: entries[0], extraCount: gesamt - 1 };
        return acc;
      }, {});
    },
  },
  methods: {
    // bei Doppelklick auf einen Tag mit Sperrzeiten das Tages-Detail-Modal angefordert
    // Tage ohne Restriktionen fordern stattdessen das Anlegen einer neuen Sperrzeit an
    onDayDoubleClick(day) {
      const info = this.infosProTag[day.key];
      if (!info) {
        this.$emit('day-add', { date: day.date });
        return;
      }
      this.$emit('day-doubleclick', { date: day.date, entries: info.entries });
    },
  },
};
</script>

<style scoped>
.restriktionen-month {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.restriktionen-month__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid var(--border);
}

.restriktionen-month__weekday {
  padding: 12px 16px;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0px;
  text-transform: uppercase;
  vertical-align: middle;
  color: var(--text-muted);
}

.restriktionen-month__grid {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

.restriktionen-month__week {
  display: grid;
  flex: 1 1 0;
  grid-template-columns: repeat(7, 1fr);
  min-height: 82px;
}

.restriktionen-month__day {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 12px;
  border-right: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  cursor: default;
}

.restriktionen-month__day:nth-child(7n) {
  border-right: none;
}

.restriktionen-month__day--has-restriktion {
  cursor: pointer;
}

.restriktionen-month__day:hover {
  background-color: var(--surface-hover);
}

.restriktionen-month__day--outside {
  background-color: var(--surface-hover);
}

.restriktionen-month__day--outside .restriktionen-month__day-number {
  color: #80808f80;
}

/* Restriktionen der Nachbarmonate bleiben sichtbar, treten aber hinter den aktuellen Monat zurück */
.restriktionen-month__day--outside .restriktionen-month__badge {
  opacity: 0.5;
}

.restriktionen-month__day--outside:hover .restriktionen-month__badge {
  opacity: 0.8;
}

.restriktionen-month__day-number {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  justify-content: center;
  min-width: 8px;
  height: 20px;
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;
  color: var(--text);
}

.restriktionen-month__day-number--today {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--primary);
  color: #ffffff;
}

.restriktionen-month__badge {
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 12px;
}

/* Monate mit sechs Wochenreihen brauchen weniger Innenabstand, damit alle Reihen sichtbar bleiben */
.restriktionen-month--kompakt .restriktionen-month__week {
  min-height: 72px;
}

.restriktionen-month--kompakt .restriktionen-month__day {
  padding: 8px 10px;
}

.restriktionen-month--kompakt .restriktionen-month__badge {
  margin-top: 12px;
}
</style>
